import { useEffect, useMemo, useRef, useState } from 'react';

import type { LogScope } from '../../platform/storage';

import { zaehleBausteine } from '../../domain/bausteine';
import { erlaubteArten, type KommentarArt } from '../../domain/kommentararten';
import { commentCount } from '../../domain/scoring';
import { bausteineFuer } from './bausteineFuer';
import { getStore } from '../../persistence/store';
import { appendAppLog } from '../../platform/appLog';
import { emptyComment, type InkStroke, type SnippetNode, type Task } from '../../domain/types';
import { useT } from '../../i18n';
import { selectAssessment, useAppStore } from '../../store/useAppStore';
import { Icon, type IconName } from '../../ui/Icon';
import { Sheet, SheetAction } from '../../ui/Sheet';
import { useToastStore } from '../../ui/Toast';
import { schliesseKommentarblatt } from './blattSchliessen';
import { sichereHandschrift } from './handwritingSave';
import { AudioTab, PhotoTab, VideoTab } from './MediaTabs';
import { LoeschAufnahmeDialog } from './LoeschAufnahmeDialog';
import styles from './CommentSheet.module.css';
import { HandwritingCanvas } from './HandwritingCanvas';

/**
 * Die Reiter heissen wie die Kommentararten des Katalogs — `domain/kommentararten.ts` führt
 * Namen **und** Reihenfolge. Hier steht nur, welches Zeichen jeder trägt.
 */
type TabKey = KommentarArt;

const TAB_ICONS: Record<TabKey, IconName> = {
  text: 'text',
  handwriting: 'pencil',
  audio: 'mic',
  photo: 'camera',
  video: 'video',
};

/** Kommentar zu einer Aufgabe: fünf Reiter über einem gemeinsamen Blatt. */
/**
 * ⚠️ **Dasselbe Blatt trägt zwei Bezüge** (seit 2026-09-13): den Kommentar zu einer Aufgabe und
 * den zur **Station**. Unterschieden wird nur, wohin die Aufnahmen geschrieben werden
 * (`logs/task-comment/…` gegen `logs/station-comment/…`) und unter welchem Schlüssel der
 * Kasten im Bogen steht — die Stationskennung ist dort so gut wie eine Aufgabenkennung.
 */
export function CommentSheet({ task, bezug = 'task-comment' }: { task: Task; bezug?: LogScope }) {
  const t = useT();
  const closeSheet = useAppStore((s) => s.closeSheet);
  const showToast = useToastStore((s) => s.show);
  const assessment = useAppStore(selectAssessment);
  const box = assessment.comments[task.id] ?? emptyComment();

  // Der offene Reiter ist reine Anzeige und bleibt deshalb hier, nicht im Store.
  const [tab, setTab] = useState<TabKey>('text');

  /**
   * Welche Reiter die Aufsicht zugelassen hat (`*_comments`, Auftrag des Nutzers,
   * 2026-09-18). Nicht jede Station braucht jede Art.
   *
   * ⚠️ **„Abgeschaltet heisst weg", auch mit Inhalt** (Entscheidung des Nutzers auf
   * ausdrückliche Rückfrage). Liegt in einer abgeschalteten Art schon eine Aufnahme, ist sie
   * hier **nicht mehr sicht- und löschbar** — sie geht aber weiter an den Server, weil der Takt
   * die Protokollzeilen unabhängig von der Oberfläche schickt. Einzelheiten und Begründung in
   * `domain/kommentararten.ts`.
   */
  const arten = useAppStore((s) => s.kommentarArten);
  const erlaubt = erlaubteArten(arten);

  /**
   * Der offene Reiter rückt nach, wenn seine Art wegfällt.
   *
   * ⚠️ **Ohne dies stünde das Blatt auf einem Reiter, den es nicht gibt**: eine Reiterzeile
   * ohne markierten Eintrag und ein leeres Feld darunter. Der Anfangswert ist `'text'`, und
   * genau Text darf abgeschaltet sein.
   */
  const offen = erlaubt.includes(tab) ? tab : erlaubt[0];

  const counts: Record<TabKey, number> = {
    text: box.text.trim() ? 1 : 0,
    handwriting: box.handwriting.length > 0 ? 1 : 0,
    audio: box.audios.length,
    photo: box.photos.length,
    video: box.videos.length,
  };

  const total = commentCount(box, arten);

  // Stand beim Öffnen des Blatts — Grundlage für den Vergleich beim Schließen unten. `useRef`,
  // nicht `useState`: der Wert soll nie einen Rerender auslösen, er ist nur ein Vergleichswert.
  // `CommentSheet` mountet frisch bei jedem Öffnen (`ActiveSheet` in `App.tsx` rendert `null`,
  // solange `sheet.kind !== 'comment'` ist, und danach dieses Blatt neu) — der Anfangswert trifft
  // also wirklich den Stand, mit dem der Prüfer die Fläche vorgefunden hat.
  const geöffnetMit = useRef(box.handwriting);

  // Geschrieben wird beim Schließen des Blatts, nicht bei jedem Strich — dafür fasst dieser
  // eine Griff alle Schließwege zusammen: den „Fertig"-Knopf ebenso wie Escape und den Klick
  // auf den Hintergrund (beide laufen über `Sheet`s `onClose`). `sichereHandschrift`
  // (`handwritingSave.ts`) vergleicht `geöffnetMit.current` gegen den aktuellen Stand selbst und
  // tut nichts, wenn sich nichts geändert hat — hier steht deshalb keine eigene Bedingung.
  /*
   * ⛔ **Das Blatt schliesst immer** — auch wenn das Schreiben scheitert.
   *
   * ⚠️ **Bis zum 2026-09-24 stand hier das Gegenteil**, und zwar mit Absicht: `await` ohne
   * Fang, damit die Funktion vor `closeSheet()` abbricht und „der Fehler nicht stillschweigend
   * verschwindet". Der Nutzer ist am iPad genau darin steckengeblieben — und die Begründung
   * trug nicht: `handleClose` hängt an **allen** Schliesswegen (Fertig-Knopf, Escape, Klick
   * daneben), ein Prüfer war also mitten in einer Prüfung im Blatt gefangen. Sichtbar wurde
   * der Fehler dabei nirgends: er landete als unbehandelte Ablehnung, ohne Einblendung und
   * ohne Zeile im Dateilog.
   *
   * Jetzt dasselbe Muster wie in `annotate/AnnotateSheet.tsx`: melden **und** schliessen. Dass
   * etwas nicht gesichert wurde, sagt die Einblendung — nicht die verschlossene Tür.
   */
  const handleClose = () =>
    schliesseKommentarblatt({
      sichere: () => sichereHandschrift(task.id, geöffnetMit.current, box.handwriting, bezug),
      toast: showToast,
      log: appendAppLog,
      close: closeSheet,
    });

  const tabLabels: Record<TabKey, string> = {
    text: t.comment.tabText,
    handwriting: t.comment.tabHandwriting,
    audio: t.comment.tabAudio,
    photo: t.comment.tabPhoto,
    video: t.comment.tabVideo,
  };

  return (
    <Sheet
      kicker={t.comment.kicker(task.short)}
      kickerIcon="comment"
      title={total === 0 ? t.comment.none : t.comment.entries(total)}
      onClose={handleClose}
      actions={
        <SheetAction icon="check" label={t.comment.done} onClick={handleClose} variant="primary" />
      }
    >
      <div className={styles.tabs}>
        {erlaubt.map((key) => (
          <button
            key={key}
            type="button"
            className={`tap ${styles.tab}`}
            /* Damit Prüfungen die Reiter ansprechen können, ohne an übersetztem Text zu
               hängen — die Beschriftung wechselt mit der Sprache, dieser Name nicht. */
            data-art={key}
            data-on={offen === key}
            onClick={() => setTab(key)}
          >
            <Icon name={TAB_ICONS[key]} size={15} />
            <span>{tabLabels[key]}</span>
            {counts[key] > 0 && <span className={styles.badge}>{counts[key]}</span>}
          </button>
        ))}
      </div>

      <div className={styles.panel}>
        {offen === 'text' && <TextTab task={task} text={box.text} bezug={bezug} />}
        {offen === 'handwriting' && <HandwritingTab task={task} strokes={box.handwriting} />}
        {offen === 'audio' && <AudioTab task={task} items={box.audios} bezug={bezug} />}
        {offen === 'photo' && <PhotoTab task={task} items={box.photos} bezug={bezug} />}
        {offen === 'video' && <VideoTab task={task} items={box.videos} bezug={bezug} />}
      </div>
    </Sheet>
  );
}

// — Reiter „Text & Bausteine" —

interface TreeRow {
  id: string;
  label: string;
  depth: number;
  isGroup: boolean;
  /** Nur bei Blättern: der Text, der eingefügt wird. */
  text?: string;
}

/** Klappt den Baum zu einer flachen Liste auf — nur die sichtbaren Zeilen. */
function flattenTree(nodes: SnippetNode[], open: Record<string, boolean>): TreeRow[] {
  const rows: TreeRow[] = [];
  const walk = (list: SnippetNode[], path: string, depth: number) => {
    list.forEach((node, i) => {
      const id = path ? path + '.' + i : String(i);
      const isGroup = !!node.children;
      rows.push({
        id,
        label: node.label ?? node.text ?? '',
        depth,
        isGroup,
        // Ein Blatt ohne eigenen Text fügt seine Beschriftung ein — im Baum steht etwa
        // „Hygiene beachtet" nur als Label da und ist trotzdem als Baustein gemeint.
        text: isGroup ? undefined : (node.text ?? node.label),
      });
      if (isGroup && open[id]) walk(node.children!, id, depth + 1);
    });
  };
  walk(nodes, '', 0);
  return rows;
}

function TextTab({ task, text, bezug }: { task: Task; text: string; bezug: LogScope }) {
  const t = useT();
  const setCommentText = useAppStore((s) => s.setCommentText);
  const [open, setOpen] = useState<Record<string, boolean>>({ '0': true, '0.0': true });
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * Die Bausteine dieser Aufgabe, aus der Ablage.
   *
   * ⚠️ **Sie sind vorgegeben** (Klarstellung des Nutzers, 2026-09-16): Der Server liefert sie
   * mit der Prüfung, das Gerät übernimmt sie nur. Bis dahin hing die Leiste an den
   * Beispieldaten (`SNIPPET_TREE`) und zeigte an jeder Station dasselbe — auch an einer, für
   * die der Server etwas ganz anderes mitgeschickt hatte.
   *
   * ⭐ **Seit dem 2026-09-20 hat auch die Station Bausteine.** Hier stand: „Beim
   * Stationskommentar zeigt `task.id` ins Leere — dort gibt es keine Aufgabe und damit keine
   * Bausteine." Das war falsch; die Relay-Seite hat es berichtigt (die Station führt eine
   * eigene Liste), und der Auftrag des Nutzers vom 2026-09-18 lautete „zuordenbar zu Station
   * **und** Aufgabe". Welche Tabelle gilt, entscheidet `bausteineFuer` am Bezug — beim
   * Stationskommentar ist `task.id` die **Stationskennung** (`app/App.tsx::alsAufgabe`).
   *
   * Ohne Bausteine bleibt die Leiste weg, statt leer dazustehen.
   */
  const [baum, setBaum] = useState<SnippetNode[] | null>(null);
  useEffect(() => {
    let lebt = true;
    void bausteineFuer(getStore(), bezug, task.id)
      .then((geladen) => {
        if (lebt) setBaum(geladen);
      })
      .catch(async (fehler: unknown) => {
        // Kein Grund, das Blatt aufzuhalten: der Freitext steht auch ohne Bausteine.
        await appendAppLog('Bausteine liessen sich nicht lesen: ' + String(fehler), 'warn');
        if (lebt) setBaum([]);
      });
    return () => {
      lebt = false;
    };
  }, [task.id, bezug]);

  const rows = useMemo(() => flattenTree(baum ?? [], open), [baum, open]);
  const leafCount = useMemo(() => zaehleBausteine(baum ?? []), [baum]);

  /** Fügt einen Baustein an der Schreibmarke ein, sonst am Ende. */
  const insert = (snippet: string) => {
    const area = areaRef.current;
    const atEnd = !area || document.activeElement !== area;
    const from = atEnd ? text.length : area.selectionStart;
    const to = atEnd ? text.length : area.selectionEnd;

    const before = text.slice(0, from);
    const after = text.slice(to);
    // Ein Baustein beginnt immer auf einer eigenen Zeile — sonst klebt er am vorigen Satz.
    const lead = before && !/\n$/.test(before) ? '\n' : '';
    const piece = lead + snippet;

    setCommentText(task.id, before + piece + after);

    if (area) {
      const caret = before.length + piece.length;
      window.requestAnimationFrame(() => {
        area.focus();
        area.setSelectionRange(caret, caret);
      });
    }
  };

  return (
    <div className={styles.textPanel} data-ohne-bausteine={leafCount === 0}>
      {/* Keine Bausteine, keine Leiste: ein leerer Kasten sähe aus, als sei etwas kaputt —
          und der Freitext bekommt den Platz, den er dann ohnehin braucht. Solange noch
          gelesen wird (`baum === null`), bleibt sie ebenfalls weg; ein Aufblitzen für einen
          Wimpernschlag ist unruhiger als ein ruhiger Aufbau. */}
      {leafCount > 0 && <div className={`${styles.card} ${styles.tree}`}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>{t.comment.snippets}</span>
          <span className={styles.cardCount}>{t.comment.snippetCount(leafCount)}</span>
        </div>
        <div className={`sc ${styles.treeList}`}>
          {rows.map((row) => {
            const used = !!row.text && text.includes(row.text);
            return (
              <button
                key={row.id}
                type="button"
                className={`tap ${styles.treeRow}`}
                data-group={row.isGroup}
                data-used={used}
                style={{ paddingLeft: 8 + row.depth * 13 }}
                onClick={() =>
                  row.isGroup
                    ? setOpen((o) => ({ ...o, [row.id]: !o[row.id] }))
                    : insert(row.text ?? '')
                }
              >
                {row.isGroup ? (
                  <span className={styles.caret} data-open={!!open[row.id]}>
                    <Icon name="chevronRight" size={12} />
                  </span>
                ) : (
                  <span className={styles.leafMark}>
                    <Icon name={used ? 'check' : 'plus'} size={12} />
                  </span>
                )}
                <span className={styles.treeLabel}>{row.label}</span>
              </button>
            );
          })}
        </div>
      </div>}

      <div className={`${styles.card} ${styles.free}`}>
        <div className={styles.cardHead}>
          <span className={styles.cardLabel}>{t.comment.freeText}</span>
          <button
            type="button"
            className={`tap ${styles.smallButton}`}
            onClick={() => setCommentText(task.id, '')}
            disabled={text.length === 0}
          >
            <Icon name="trash" size={15} />
            <span>{t.comment.clear}</span>
          </button>
        </div>
        <textarea
          ref={areaRef}
          className={styles.textarea}
          value={text}
          onChange={(e) => setCommentText(task.id, e.target.value)}
          placeholder={leafCount > 0 ? t.comment.freeTextHint : t.comment.freeTextHintOhne}
        />
      </div>
    </div>
  );
}

// — Reiter „Handschrift" —

function HandwritingTab({ task, strokes }: { task: Task; strokes: InkStroke[] }) {
  const t = useT();
  const addStroke = useAppStore((s) => s.addHandwritingStroke);
  const clear = useAppStore((s) => s.clearHandwriting);
  /**
   * ⚠️ **Ein Tipp verwarf bis zum 2026-09-13 alle Striche auf einmal**, ohne Rückfrage und
   * ohne Weg zurück. Anders als Ton, Foto und Video liegt die Handschrift **nicht** als Datei
   * vor, sondern als Strichliste im Bogen — es gibt hier also auch kein Archiv, das den
   * Fehlgriff auffinge. Umso mehr braucht es die Frage.
   */
  const [fragt, setFragt] = useState(false);

  return (
    <>
      {fragt && (
        <LoeschAufnahmeDialog
          art="handwriting"
          beschreibung={t.comment.handwritingStrokes(strokes.length)}
          onConfirm={() => clear(task.id)}
          onClose={() => setFragt(false)}
        />
      )}
      <div className={styles.inkHead}>
        <span className={styles.inkTitle}>{t.comment.handwritingTitle}</span>
        <button
          type="button"
          className={`tap ${styles.inkButton}`}
          onClick={() => setFragt(true)}
          disabled={strokes.length === 0}
        >
          <Icon name="trash" size={15} />
          <span>{t.comment.clear}</span>
        </button>
      </div>
      <div className={styles.inkSheet}>
        {strokes.length === 0 && (
          <span className={styles.inkHint}>{t.comment.handwritingHint}</span>
        )}
        <HandwritingCanvas strokes={strokes} onStroke={(s) => addStroke(task.id, s)} />
      </div>
    </>
  );
}
